import { useSettings } from "../../context/SettingsContext";
import { Toggle, SettingRow, SectionCard, NumberInput, InfoBox, ButtonGroup } from "../ui/index";

const MOBILE_PG_POSITIONS = [
    { value: "bottom", label: "Bottom" },
    { value: "left",   label: "Left"   },
    { value: "right",  label: "Right"  },
];

const MOBILE_THUMB_POSITIONS = [
    { value: "bottom", label: "Bottom" },
    { value: "top",    label: "Top"    },
    { value: "left",   label: "Left"   },
    { value: "right",  label: "Right"  },
];

export default function MobilePanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title="Mobile Fallback"
                description="Below this breakpoint, Anchor Navigation and Sticky Split galleries collapse to a standard carousel. The Thumbnail Gallery applies mobile-specific arrow, pagination, and thumbnail settings instead."
            >
                <SettingRow
                    label="Mobile Breakpoint"
                    description="Viewport width below which mobile settings take effect. Anchor and Sticky Split switch to a standard carousel; Thumbnail Gallery applies the mobile navigation and thumbnail options below. Set to 0 to disable."
                >
                    <NumberInput
                        value={settings.mobileBreakpoint}
                        onChange={(v) => s("mobileBreakpoint", v)}
                        min={0}
                        max={1280}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                {settings.mobileBreakpoint > 0 && (
                    <>
                        <SettingRow
                            label="Navigation Arrows"
                            description="Show previous/next arrow buttons on the mobile carousel. Independent of the desktop arrow setting."
                        >
                            <Toggle
                                value={settings.mobileArrows !== false}
                                onChange={(v) => s("mobileArrows", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Dot Pagination"
                            description="Show dot indicators below the mobile carousel for quick navigation."
                        >
                            <Toggle
                                value={settings.mobilePagination !== false}
                                onChange={(v) => s("mobilePagination", v)}
                            />
                        </SettingRow>

                        {settings.mobilePagination !== false && (
                            <SettingRow
                                label="Dot Position"
                                description="Where the pagination dots appear in the mobile carousel."
                            >
                                <ButtonGroup
                                    options={MOBILE_PG_POSITIONS}
                                    value={settings.mobilePaginationPosition || "bottom"}
                                    onChange={(v) => s("mobilePaginationPosition", v)}
                                />
                            </SettingRow>
                        )}

                        <SettingRow
                            label="Show Thumbnails"
                            description="Show the thumbnail strip in the mobile carousel fallback. Off by default for a cleaner mobile experience."
                        >
                            <Toggle
                                value={settings.mobileShowThumbs}
                                onChange={(v) => s("mobileShowThumbs", v)}
                            />
                        </SettingRow>

                        {settings.mobileShowThumbs && (
                            <>
                                <SettingRow
                                    label="Thumbnail Position"
                                    description="Where the thumbnail strip appears on mobile. Independent of the desktop thumbnail position."
                                >
                                    <ButtonGroup
                                        options={MOBILE_THUMB_POSITIONS}
                                        value={settings.mobileThumbnailPosition || "bottom"}
                                        onChange={(v) => s("mobileThumbnailPosition", v)}
                                    />
                                </SettingRow>

                                <SettingRow
                                    label="Thumbnail Width"
                                    description="Width of each thumbnail item on mobile. Set to 0 to use the desktop thumbnail width."
                                >
                                    <NumberInput
                                        value={settings.mobileThumbnailWidth}
                                        onChange={(v) => s("mobileThumbnailWidth", v)}
                                        min={0}
                                        max={300}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>

                                <SettingRow
                                    label="Thumbnail Height"
                                    description="Height of each thumbnail item on mobile. Set to 0 to use the desktop thumbnail height."
                                >
                                    <NumberInput
                                        value={settings.mobileThumbnailHeight}
                                        onChange={(v) => s("mobileThumbnailHeight", v)}
                                        min={0}
                                        max={300}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>
                            </>
                        )}
                    </>
                )}

            </SectionCard>

            <SectionCard
                title="Touch Zoom"
                description="Pinch-to-zoom behavior on touch devices."
            >
                <SettingRow
                    label="Enable Touch Zoom"
                    description="Allow users to pinch to zoom the main image on mobile and tablet devices."
                >
                    <Toggle
                        value={settings.touchZoom}
                        onChange={(v) => s("touchZoom", v)}
                    />
                </SettingRow>

                {settings.touchZoom && (
                    <>
                        <SettingRow
                            label="Minimum Scale"
                            description="Minimum zoom level (1 = original size)."
                        >
                            <NumberInput
                                value={settings.touchZoomMin}
                                onChange={(v) => s("touchZoomMin", v)}
                                min={1}
                                max={3}
                                step={0.1}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Maximum Scale"
                            description="Maximum zoom level users can reach by pinching."
                        >
                            <NumberInput
                                value={settings.touchZoomMax}
                                onChange={(v) => s("touchZoomMax", v)}
                                min={1.5}
                                max={10}
                                step={0.5}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title="Swipe Navigation"
                description="Swipe gesture settings for navigating the gallery."
            >
                <SettingRow
                    label="Swipe Threshold"
                    description="Minimum swipe distance in pixels required to trigger a gallery slide."
                >
                    <NumberInput
                        value={settings.swipeThreshold}
                        onChange={(v) => s("swipeThreshold", v)}
                        min={10}
                        max={200}
                        unit="px"
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}

